import React, { useRef, useState } from "react"

import { Badge } from "@coval/ui"
import { Trash2 } from "lucide-react"

import { OBJECT_VIEW_MODE } from "@/lib/viewModes"
import { DIFF_MODE, InlineDiff } from "@/components/diff/InlineDiff"
import { HighlightedText } from "@/components/HighlightText"
import { JsonEditor } from "@/components/JsonEditor"
import type { JsonObject } from "@/types/json"
import { cn } from "@/utils/cn"

import { useFindReplaceContext } from "../../../contexts/FindReplaceContext"
import { useTestSetEditorContext } from "../../../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../../../event-bus"
import type { EditableTestCase } from "../../../model"
import { getHighlightState } from "../../../utils/findReplaceHighlight"
import { getPendingEditForTestCase } from "../../../utils/pendingChangesValidation"
import { focusNestedEditor } from "./focusNestedEditor"
import { formatMetadataValue } from "./formatMetadataValue"
import { HighlightedContentEditable } from "./HighlightedContentEditable"
import { useGridCell } from "./useGridCell"

interface TestCaseMetadataCellProps {
  testCase: EditableTestCase
  isExpanded: boolean
  width: string
  isMarkedForDeletion?: boolean
}

type DiffStatus = "added" | "removed" | "changed" | "unchanged"

function getKeyDiffState(
  key: string,
  metadata: JsonObject,
  pendingMetadata: JsonObject | null | undefined,
): { status: DiffStatus; oldValue: string; newValue: string } {
  const oldValue = formatMetadataValue(metadata[key])
  if (pendingMetadata === undefined) {
    return { status: "unchanged", oldValue, newValue: oldValue }
  }

  // null = clear all metadata
  const resolved = pendingMetadata ?? {}
  const inOld = key in metadata
  const inNew = key in resolved
  const newValue = formatMetadataValue(resolved[key])

  if (!inOld && inNew) {
    return { status: "added", oldValue: "", newValue }
  }
  if (inOld && !inNew) {
    return { status: "removed", oldValue, newValue: "" }
  }
  if (oldValue !== newValue) {
    return { status: "changed", oldValue, newValue }
  }
  return { status: "unchanged", oldValue, newValue: oldValue }
}

const DIFF_BG: Record<DiffStatus, string> = {
  added: "bg-green-50 dark:bg-green-900/20",
  removed: "bg-red-50 dark:bg-red-900/20",
  changed: "bg-surface-1",
  unchanged: "bg-surface-1",
}

const DIFF_KEY_TEXT: Record<DiffStatus, string> = {
  added: "text-green-600 dark:text-green-400 font-bold",
  removed: "text-red-500 line-through font-bold",
  changed: "text-coval-text-muted",
  unchanged: "text-coval-text-muted",
}

const MetadataKeyValueRow: React.FC<{
  entryKey: string
  status: DiffStatus
  oldValue: string
  newValue: string
}> = ({ entryKey, status, oldValue, newValue }) => (
  <div className={cn("rounded p-2 min-h-8", DIFF_BG[status])}>
    <div
      className={cn(
        "text-xs pb-1 mb-1 border-b border-coval-border-active break-words",
        DIFF_KEY_TEXT[status],
      )}
    >
      {entryKey}
    </div>
    <div className="text-sm whitespace-pre-wrap break-words">
      {status === "added" && (
        <span className="text-green-700 dark:text-green-400">{newValue}</span>
      )}
      {status === "removed" && (
        <span className="text-red-700 dark:text-red-400 line-through">
          {oldValue}
        </span>
      )}
      {status === "changed" && (
        <InlineDiff oldText={oldValue} newText={newValue} />
      )}
      {status === "unchanged" && (
        <span className="text-coval-text">{oldValue}</span>
      )}
    </div>
  </div>
)

export const TestCaseMetadataCell: React.FC<TestCaseMetadataCellProps> = ({
  testCase,
  isExpanded,
  width,
  isMarkedForDeletion = false,
}) => {
  const dispatch = useEventDispatcher()
  const { pendingChanges, metadataViewMode } = useTestSetEditorContext()
  const { findReplaceState } = useFindReplaceContext()
  const pendingEdit = getPendingEditForTestCase(testCase.id, pendingChanges)
  const [newKey, setNewKey] = useState("")
  const [newValue, setNewValue] = useState("")
  const [isAddingMetadata, setIsAddingMetadata] = useState(false)
  const newKeyRef = useRef<HTMLInputElement>(null)

  const { isEditing, cellProps } = useGridCell({
    ariaLabel: "Edit metadata",
    portalAware: true,
  })

  const metadata: JsonObject = testCase.expected_output_json || {}
  const pendingMetadata = pendingEdit?.expected_output_json
  const hasPendingMetadata = pendingMetadata !== undefined
  const entries = Object.entries(metadata)

  // All keys across current and pending metadata (for diff views)
  const editableKeys = hasPendingMetadata
    ? [
        ...new Set([
          ...Object.keys(metadata),
          ...Object.keys(pendingMetadata || {}),
        ]),
      ]
    : Object.keys(metadata)

  const handleUpdateMetadata = (metadata: JsonObject | null) => {
    dispatch(EVENTS.TEST_CASE_UPDATE, {
      testCaseId: testCase.id,
      updates: { expected_output_json: metadata },
    })
  }

  const handleKeyBlur = (oldKey: string, element: HTMLDivElement | null) => {
    if (!element) {
      return
    }
    const newKey = element.textContent?.trim() || ""

    if (newKey !== oldKey && newKey) {
      const updatedMetadata = { ...metadata }
      const value = updatedMetadata[oldKey]
      delete updatedMetadata[oldKey]
      updatedMetadata[newKey] = value
      handleUpdateMetadata(updatedMetadata)
    }
  }

  const handleValueBlur = (
    key: string,
    element: HTMLTextAreaElement | HTMLDivElement | null,
  ) => {
    if (!element) {
      return
    }
    const newValue =
      element instanceof HTMLTextAreaElement
        ? element.value
        : element.textContent || ""

    if (newValue !== formatMetadataValue(metadata[key])) {
      const updatedMetadata = {
        ...metadata,
        [key]: newValue,
      }
      handleUpdateMetadata(updatedMetadata)
    }
  }

  const handleValueChange = (key: string, nextValue: string) => {
    const currentValue = metadata[key]
    if (nextValue === formatMetadataValue(currentValue)) {
      return
    }

    const updatedMetadata = {
      ...metadata,
      [key]: nextValue,
    }
    handleUpdateMetadata(updatedMetadata)
  }

  const handleDeletePair = (key: string) => {
    const updatedMetadata = { ...metadata }
    delete updatedMetadata[key]
    handleUpdateMetadata(
      Object.keys(updatedMetadata).length > 0 ? updatedMetadata : null,
    )
  }

  const handleStartAddMetadata = () => {
    setIsAddingMetadata(true)
    setTimeout(() => newKeyRef.current?.focus(), 0)
  }

  const clearDraftMetadata = () => {
    setIsAddingMetadata(false)
    setNewKey("")
    setNewValue("")
  }

  const commitDraftMetadataIfValid = () => {
    const key = newKey.trim()
    if (!key) {
      clearDraftMetadata()
      return
    }

    const updatedMetadata = {
      ...metadata,
      [key]: newValue,
    }
    handleUpdateMetadata(updatedMetadata)
    clearDraftMetadata()
  }

  const handleJsonChange = (newMetadata: Record<string, unknown>) => {
    const finalMetadata =
      Object.keys(newMetadata).length > 0 ? (newMetadata as JsonObject) : null
    handleUpdateMetadata(finalMetadata)
  }

  const renderCollapsedContent = () => {
    const hasMetadata = entries.length > 0
    if (!hasMetadata && !hasPendingMetadata) {
      return (
        <span className="text-sm text-coval-text-muted select-none">
          No metadata
        </span>
      )
    }

    // When there's a pending metadata change, show a compact diff summary
    if (hasPendingMetadata) {
      return (
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-wrap items-center gap-1 w-full min-w-0">
            {editableKeys.slice(0, 4).map((key) => {
              const { status, oldValue, newValue } = getKeyDiffState(
                key,
                metadata,
                pendingMetadata,
              )
              const displayValue =
                status === "removed" ? oldValue : newValue || oldValue

              return (
                <Badge
                  key={key}
                  size="status"
                  variant="default"
                  className={cn(
                    "max-w-full min-w-0 shrink truncate",
                    status === "added" && "bg-green-100 dark:bg-green-900/30",
                    status === "removed" &&
                      "bg-red-100 dark:bg-red-900/30 line-through",
                    status === "changed" &&
                      "bg-yellow-100 dark:bg-yellow-900/30",
                  )}
                  title={`${key}: ${displayValue}`}
                >
                  <span className="block truncate">
                    {key}: {displayValue}
                  </span>
                </Badge>
              )
            })}
            {editableKeys.length > 4 && (
              <Badge
                size="status"
                variant="default"
                className="shrink-0 truncate"
              >
                +{editableKeys.length - 4} more
              </Badge>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-2 w-full">
        {hasMetadata && (
          <div className="flex flex-wrap items-center gap-1 w-full min-w-0">
            {entries.slice(0, 3).map(([key, value]) => {
              const keyHighlighting = getHighlightState(
                findReplaceState,
                testCase.id,
                "expected_output_json",
                `${key}:key`,
              )
              const valueHighlighting = getHighlightState(
                findReplaceState,
                testCase.id,
                "expected_output_json",
                `${key}:value`,
              )
              const displayValue =
                typeof value === "object"
                  ? JSON.stringify(value)
                  : String(value)

              return (
                <Badge
                  key={key}
                  size="status"
                  variant="default"
                  className="max-w-full min-w-0 shrink truncate"
                  title={`${key}: ${displayValue}`}
                >
                  <span className="block truncate">
                    {keyHighlighting.shouldHighlight ? (
                      <HighlightedText
                        text={key}
                        searchText={keyHighlighting.highlightText!}
                        isCurrentMatch={keyHighlighting.isCurrentMatch}
                      />
                    ) : (
                      key
                    )}
                    {": "}
                    {valueHighlighting.shouldHighlight ? (
                      <HighlightedText
                        text={displayValue}
                        searchText={valueHighlighting.highlightText!}
                        isCurrentMatch={valueHighlighting.isCurrentMatch}
                      />
                    ) : (
                      displayValue
                    )}
                  </span>
                </Badge>
              )
            })}
            {entries.length > 3 && (
              <Badge
                size="status"
                variant="default"
                className="shrink-0 truncate"
              >
                +{entries.length - 3} more
              </Badge>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderExpandedContent = () => {
    if (!isEditing) {
      // Expanded read-only: show all key-value pairs without edit controls
      const hasMetadata = entries.length > 0

      if (!hasMetadata && !hasPendingMetadata) {
        return (
          <span className="text-sm text-coval-text-muted select-none">
            No metadata
          </span>
        )
      }

      // When there's a pending metadata change, show diffs in expanded read-only mode
      if (hasPendingMetadata) {
        const resolvedPending = pendingMetadata || {}

        if (metadataViewMode === OBJECT_VIEW_MODE.JSON) {
          return (
            <div className="flex flex-col gap-4 w-full flex-1">
              <InlineDiff
                mode={DIFF_MODE.CHAR}
                oldText={JSON.stringify(metadata, null, 2)}
                newText={JSON.stringify(resolvedPending, null, 2)}
              />
            </div>
          )
        }

        return (
          <div className="flex flex-col gap-4 w-full flex-1">
            <div className="flex flex-col gap-2">
              {editableKeys.map((key) => {
                const diff = getKeyDiffState(key, metadata, pendingMetadata)
                return (
                  <MetadataKeyValueRow key={key} entryKey={key} {...diff} />
                )
              })}
            </div>
          </div>
        )
      }

      return (
        <div className="flex flex-col gap-4 w-full flex-1">
          {hasMetadata &&
            (metadataViewMode === OBJECT_VIEW_MODE.JSON ? (
              <div className="text-sm whitespace-pre-wrap text-coval-text break-words">
                {JSON.stringify(metadata, null, 2)}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {entries.map(([key, value]) => {
                  const displayValue =
                    typeof value === "object"
                      ? JSON.stringify(value, null, 2)
                      : String(value)
                  return (
                    <div key={key} className="bg-surface-1 rounded p-2 min-h-8">
                      <div className="text-xs text-coval-text-muted pb-1 mb-1 border-b border-coval-border-active font-bold break-words">
                        {key}
                      </div>
                      <div className="text-sm whitespace-pre-wrap text-coval-text break-words">
                        {displayValue}
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
        </div>
      )
    }

    // Edit mode: full edit controls
    return (
      <div
        className="flex flex-col gap-4 w-full flex-1"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          {metadataViewMode === OBJECT_VIEW_MODE.KEY_VALUE ? (
            <div className="flex flex-col gap-2">
              {editableKeys.map((key) => {
                const { status, oldValue, newValue } = getKeyDiffState(
                  key,
                  metadata,
                  pendingMetadata,
                )
                const isDiffRow = status !== "unchanged"

                if (isDiffRow) {
                  return (
                    <div key={key} className="flex gap-2 items-start">
                      <MetadataKeyValueRow
                        entryKey={key}
                        status={status}
                        oldValue={oldValue}
                        newValue={newValue}
                      />
                    </div>
                  )
                }

                const keyHighlighting = getHighlightState(
                  findReplaceState,
                  testCase.id,
                  "expected_output_json",
                  `${key}:key`,
                )
                const valueHighlighting = getHighlightState(
                  findReplaceState,
                  testCase.id,
                  "expected_output_json",
                  `${key}:value`,
                )

                return (
                  <div key={key} className="flex gap-2 items-start group">
                    <div
                      className="flex-1 bg-surface-1 transition-colors rounded p-2 min-h-8 max-w-full cursor-text"
                      onClick={focusNestedEditor}
                    >
                      <div className="text-xs text-coval-text-muted pb-1 mb-1 border-b border-coval-border-active break-words">
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => handleKeyBlur(key, e.currentTarget)}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-block outline-none bg-transparent font-bold break-words max-w-full"
                        >
                          {keyHighlighting.shouldHighlight ? (
                            <HighlightedText
                              text={key}
                              searchText={keyHighlighting.highlightText!}
                              isCurrentMatch={keyHighlighting.isCurrentMatch}
                            />
                          ) : (
                            key
                          )}
                        </div>
                      </div>
                      {valueHighlighting.shouldHighlight ? (
                        <HighlightedContentEditable
                          value={oldValue}
                          onChange={(v) => handleValueChange(key, v)}
                          onBlur={(el) => handleValueBlur(key, el)}
                          highlightText={valueHighlighting.highlightText!}
                          isCurrentMatch={valueHighlighting.isCurrentMatch}
                          className="w-full text-sm whitespace-pre-wrap outline-none text-coval-text bg-transparent resize-y min-h-10 break-words max-w-full"
                          style={{ minHeight: "2.5rem" }}
                        />
                      ) : (
                        <textarea
                          value={oldValue}
                          onChange={(e) =>
                            handleValueChange(key, e.target.value)
                          }
                          onBlur={(e) => handleValueBlur(key, e.currentTarget)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full text-sm whitespace-pre-wrap outline-none text-coval-text bg-transparent resize-y min-h-10 break-words max-w-full"
                        />
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeletePair(key)
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-coval-danger-text hover:text-coval-danger-text p-1 mt-1"
                      title="Delete metadata"
                      aria-label="Delete metadata"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )
              })}

              {/* Add new key */}
              {!hasPendingMetadata && (
                <>
                  {!isAddingMetadata ? (
                    <div className="flex gap-2 items-start">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleStartAddMetadata()
                        }}
                        className="flex-1 px-2 py-2 text-sm text-coval-text-muted hover:text-coval-text hover:bg-surface-1 rounded transition-colors text-left"
                      >
                        + Add metadata
                      </button>
                      <div className="w-6 shrink-0" />
                    </div>
                  ) : (
                    <div className="flex gap-2 items-start">
                      <div
                        className="flex-1 bg-surface-1 rounded p-2 transition-colors max-w-full cursor-text"
                        onClick={focusNestedEditor}
                      >
                        <div className="pb-1 mb-2 border-b border-coval-border-active break-words">
                          <input
                            ref={newKeyRef}
                            type="text"
                            value={newKey}
                            onChange={(e) => setNewKey(e.target.value)}
                            onBlur={commitDraftMetadataIfValid}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full text-xs outline-none bg-transparent text-coval-text-muted font-bold placeholder:text-coval-text-muted max-w-full"
                            placeholder="metadata_key"
                            aria-label="Metadata key"
                          />
                        </div>
                        <input
                          type="text"
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          onBlur={commitDraftMetadataIfValid}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full text-sm outline-none bg-transparent placeholder:text-coval-text-muted max-w-full"
                          placeholder="value"
                          aria-label="Metadata value"
                        />
                      </div>
                      <div className="w-6 shrink-0" />
                    </div>
                  )}
                </>
              )}
            </div>
          ) : hasPendingMetadata ? (
            /* JSON mode with pending diff */
            <InlineDiff
              mode={DIFF_MODE.CHAR}
              oldText={JSON.stringify(metadata, null, 2)}
              newText={JSON.stringify(pendingMetadata || {}, null, 2)}
            />
          ) : (
            /* JSON mode */
            <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
              <JsonEditor
                value={metadata}
                onChange={handleJsonChange}
                ariaLabel="Metadata JSON"
                height={`${Math.max(160, (JSON.stringify(metadata, null, 2).split("\n").length + 1) * 19)}px`}
                allowEmpty
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <td
      {...cellProps}
      className="p-0 outline-none"
      style={{ width, padding: 0, verticalAlign: "top" }}
    >
      <div
        className={cn(
          "px-2 pt-4 pb-2 flex flex-col",
          isMarkedForDeletion && "opacity-50 line-through",
        )}
      >
        {isExpanded || isEditing
          ? renderExpandedContent()
          : renderCollapsedContent()}
      </div>
    </td>
  )
}
